import {
  Box,
  Button,
  Icon,
  Popover,
  Select,
  TextField,
  useBreakpoints,
  VerticalStack,
  HorizontalStack,
  HorizontalGrid,
  Scrollable,
  OptionList,
  DatePicker,
} from '@shopify/polaris';
import { ArrowRightMinor, CalendarMinor } from '@shopify/polaris-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useTimezone } from '../../../customHooks/useTimezone';
import { toYMD } from '../../../helpers/dates';

// Construye un Date con componentes locales del navegador equivalentes al
// día civil de `dt` en la zona seleccionada. Los calendar pickers del
// navegador (Polaris DatePicker) trabajan siempre con Dates interpretados en
// zona del OS, así que para que el usuario vea "10 abril" independientemente
// de su zona de sistema, construimos el Date a partir de los componentes
// civiles de la zona objetivo.
const civilDayAsLocalDate = (dt) => new Date(dt.year, dt.month - 1, dt.day);

const DateRangePicker = ({ onDateChanges, defaultDates }) => {
  const { mdDown, lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const { timezone } = useTimezone();

  const { today, ranges } = useMemo(() => {
    const nowInTz = DateTime.now().setZone(timezone).startOf('day');
    const todayLocal = civilDayAsLocalDate(nowInTz);
    const yesterdayLocal = civilDayAsLocalDate(nowInTz.minus({ days: 1 }));
    const at = (luxonDt) => civilDayAsLocalDate(luxonDt);

    const computedRanges = [
      {
        title: 'Últimos 365 días',
        alias: 'last365',
        period: { since: at(nowInTz.minus({ days: 365 })), until: yesterdayLocal },
      },
      {
        title: 'Últimos 90 días',
        alias: 'last90',
        period: { since: at(nowInTz.minus({ days: 90 })), until: yesterdayLocal },
      },
      {
        title: 'Últimos 30 días',
        alias: 'last30',
        period: { since: at(nowInTz.minus({ days: 30 })), until: yesterdayLocal },
      },
      {
        title: 'Últimos 7 días',
        alias: 'last7',
        period: { since: at(nowInTz.minus({ days: 7 })), until: yesterdayLocal },
      },
      {
        title: 'Ayer',
        alias: 'yesterday',
        period: { since: yesterdayLocal, until: yesterdayLocal },
      },
      {
        title: 'Último período de facturación',
        alias: 'lastMonth',
        period: (() => {
          // El período de facturación va del día 16 al día 15 del mes siguiente.
          // Si estamos antes del día 15, el último período es el que terminó
          // el 15 del mes actual; si estamos a partir del 16, el que terminó
          // el 15 del mes pasado.
          const beforeCutoff = nowInTz.day < 15;
          const end = beforeCutoff
            ? nowInTz.minus({ months: 1 }).set({ day: 15 })
            : nowInTz.set({ day: 15 });
          const start = end.minus({ months: 1 }).set({ day: 16 });
          return { since: at(start), until: at(end) };
        })(),
      },
      {
        title: 'Último mes natural',
        alias: 'lastNaturalMonth',
        period: (() => {
          const lastMonth = nowInTz.minus({ months: 1 });
          return { since: at(lastMonth.startOf('month')), until: at(lastMonth.endOf('month')) };
        })(),
      },
      {
        title: 'Desde el inicio de año',
        alias: 'sinceStartOfYear',
        period: { since: at(nowInTz.startOf('year')), until: yesterdayLocal },
      },
    ];

    return { today: todayLocal, ranges: computedRanges };
  }, [timezone]);

  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(ranges[0]);
  const [inputValues, setInputValues] = useState({});
  const [{ month, year }, setDate] = useState({
    month: activeDateRange.period.since.getMonth(),
    year: activeDateRange.period.since.getFullYear(),
  });
  const datePickerRef = useRef(null);
  const VALID_YYYY_MM_DD_DATE_REGEX = /^\d{4}-\d{1,2}-\d{1,2}/;
  function isDate(date) {
    return !isNaN(new Date(date).getDate());
  }
  function isValidYearMonthDayDateString(date) {
    return VALID_YYYY_MM_DD_DATE_REGEX.test(date) && isDate(date);
  }
  function isValidDate(date) {
    return date.length === 10 && isValidYearMonthDayDateString(date);
  }
  function parseYearMonthDayDateString(input) {
    const [year, month, day] = input.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  function formatDateToYearMonthDayDateString(date, reverse = true) {
    const year = String(date.getFullYear());
    let month = String(date.getMonth() + 1);
    let day = String(date.getDate());
    if (month.length < 2) {
      month = String(month).padStart(2, '0');
    }
    if (day.length < 2) {
      day = String(day).padStart(2, '0');
    }

    return reverse ? [year, month, day].reverse().join('-') : [year, month, day].join('-');
  }
  const formatDate = formatDateToYearMonthDayDateString;

  function nodeContainsDescendant(rootNode, descendant) {
    if (rootNode === descendant) {
      return true;
    }
    let parent = descendant.parentNode;
    while (parent != null) {
      if (parent === rootNode) {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  }
  function isNodeWithinPopover(node) {
    return datePickerRef?.current ? nodeContainsDescendant(datePickerRef.current, node) : false;
  }
  function handleStartInputValueChange(value) {
    setInputValues((prevState) => {
      return { ...prevState, since: value };
    });
    if (isValidDate(value)) {
      const newSince = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newSince <= prevState.period.until
            ? { since: newSince, until: prevState.period.until }
            : { since: newSince, until: newSince };
        return {
          ...prevState,
          period: newPeriod,
        };
      });
    }
  }
  function handleEndInputValueChange(value) {
    setInputValues((prevState) => ({ ...prevState, until: value }));
    if (isValidDate(value)) {
      const newUntil = parseYearMonthDayDateString(value);
      setActiveDateRange((prevState) => {
        const newPeriod =
          prevState.period && newUntil >= prevState.period.since
            ? { since: prevState.period.since, until: newUntil }
            : { since: newUntil, until: newUntil };
        return {
          ...prevState,
          period: newPeriod,
        };
      });
    }
  }
  function handleInputBlur({ relatedTarget }) {
    const isRelatedTargetWithinPopover = relatedTarget != null && isNodeWithinPopover(relatedTarget);
    // If focus moves from the TextField to the Popover
    // we don't want to close the popover
    if (isRelatedTargetWithinPopover) {
      return;
    }
    setPopoverActive(false);
  }
  function handleMonthChange(month, year) {
    setDate({ month, year });
  }
  function handleCalendarChange({ start, end }) {
    const newDateRange = ranges.find((range) => {
      return range.period.since.valueOf() === start.valueOf() && range.period.until.valueOf() === end.valueOf();
    }) || {
      alias: 'custom',
      title: 'Custom',
      period: {
        since: start,
        until: end,
      },
    };
    setActiveDateRange(newDateRange);
  }
  function apply() {
    setPopoverActive(false);
  }
  function cancel() {
    setPopoverActive(false);
  }

  const handleActiveDateRange = (validPeriodRange) => {
    setInputValues({
      since: formatDate(validPeriodRange.since),
      until: formatDate(validPeriodRange.until),
    });
    function monthDiff(referenceDate, newDate) {
      return newDate.month - referenceDate.month + 12 * (referenceDate.year - newDate.year);
    }
    const monthDifference = monthDiff(
      { year, month },
      {
        year: validPeriodRange.until.getFullYear(),
        month: validPeriodRange.until.getMonth(),
      },
    );
    if (monthDifference > 1 || monthDifference < 0) {
      setDate({
        month: validPeriodRange.until.getMonth(),
        year: validPeriodRange.until.getFullYear(),
      });
    }
  };

  useEffect(() => {
    if (activeDateRange) {
      handleActiveDateRange(activeDateRange.period);

      onDateChanges({
        since: toYMD(activeDateRange.period.since),
        until: toYMD(activeDateRange.period.until),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDateRange, timezone]);

  useEffect(() => {
    if (defaultDates) {
      handleCalendarChange({
        start: defaultDates.since,
        end: defaultDates.until,
      });
      onDateChanges({
        since: toYMD(defaultDates.since),
        until: toYMD(defaultDates.until),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDates, timezone]);

  const buttonValue =
    activeDateRange.title === 'Custom'
      ? activeDateRange.period.since.toLocaleDateString('es-Es') +
        ' - ' +
        activeDateRange.period.until.toLocaleDateString('es-Es')
      : activeDateRange.title;
  return (
    <Popover
      active={popoverActive}
      autofocusTarget="none"
      preferredAlignment="left"
      preferredPosition="below"
      fluidContent
      sectioned={false}
      fullHeight
      activator={
        <Button icon={CalendarMinor} onClick={() => setPopoverActive(!popoverActive)}>
          {buttonValue}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <HorizontalGrid
          columns={{
            xs: '1fr',
            mdDown: '1fr',
            md: 'max-content max-content',
          }}
          gap={0}
          ref={datePickerRef}
        >
          <Box
            maxWidth={mdDown ? '516px' : '212px'}
            width={mdDown ? '100%' : '212px'}
            padding={{ xs: 5, md: 0 }}
            paddingBlockEnd={{ xs: 1, md: 0 }}
          >
            {mdDown ? (
              <Select
                label="dateRangeLabel"
                labelHidden
                onChange={(value) => {
                  const result = ranges.find(({ title, alias }) => title === value || alias === value);
                  setActiveDateRange(result);
                }}
                value={activeDateRange?.title || activeDateRange?.alias || ''}
                options={ranges.map(({ alias, title }) => title || alias)}
              />
            ) : (
              <Scrollable style={{ height: '334px' }}>
                <OptionList
                  options={ranges.map((range) => ({
                    value: range.alias,
                    label: range.title,
                  }))}
                  selected={activeDateRange.alias}
                  onChange={(value) => {
                    setActiveDateRange(ranges.find((range) => range.alias === value[0]));
                  }}
                />
              </Scrollable>
            )}
          </Box>
          <Box padding={{ xs: 5 }} maxWidth={mdDown ? '320px' : '516px'}>
            <VerticalStack gap="4">
              <HorizontalStack gap="2">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={'Desde'}
                    labelHidden
                    prefix={<Icon source={CalendarMinor} />}
                    value={inputValues.since}
                    onChange={handleStartInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
                <Icon source={ArrowRightMinor} />
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    role="combobox"
                    label={'Hasta'}
                    labelHidden
                    prefix={<Icon source={CalendarMinor} />}
                    value={inputValues.until}
                    onChange={handleEndInputValueChange}
                    onBlur={handleInputBlur}
                    autoComplete="off"
                  />
                </div>
              </HorizontalStack>
              <div>
                <DatePicker
                  month={month}
                  year={year}
                  selected={{
                    start: activeDateRange.period.since,
                    end: activeDateRange.period.until,
                  }}
                  onMonthChange={handleMonthChange}
                  onChange={handleCalendarChange}
                  multiMonth={shouldShowMultiMonth}
                  allowRange
                  weekStartsOn={1}
                  disableDatesAfter={today}
                />
              </div>
            </VerticalStack>
          </Box>
        </HorizontalGrid>
      </Popover.Pane>
      <Popover.Pane fixed>
        <Popover.Section>
          <HorizontalStack align="end">
            <Button onClick={cancel}>Cancel</Button>
            <Button primary onClick={apply}>
              Apply
            </Button>
          </HorizontalStack>
        </Popover.Section>
      </Popover.Pane>
    </Popover>
  );
};

export default DateRangePicker;
